import React, { useState, useEffect } from 'react';
import { cashMovementApi } from '@/services/api';
import { Loader2, ArrowDownToLine, ArrowUpFromLine, X, AlertTriangle } from 'lucide-react';
import { CurrencyIcon } from '@/components/common/CurrencyIcon';
import toast from 'react-hot-toast';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ManualCashEntryModal({ open, onClose, onSuccess }: Props) {
  const [transactionType, setTransactionType] = useState<'Cash In' | 'Cash Out'>('Cash In');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setTransactionType('Cash In');
      setAmount('');
      setRemarks('');
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await cashMovementApi.create({
        movement_type: transactionType === 'Cash In' ? 'Paid In' : 'Paid Out',
        amount: amt,
        reason: transactionType === 'Cash In' ? 'Manual Cash Entry' : 'Manual Cash Withdrawal',
        remarks,
      });
      toast.success(`${transactionType} successful`);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || `Failed to process ${transactionType.toLowerCase()}`;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b shrink-0 bg-emerald-50 border-emerald-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2.5 rounded-xl shrink-0 bg-emerald-100 text-emerald-700">
              {transactionType === 'Cash In' ? <ArrowDownToLine className="w-5 h-5" /> : <ArrowUpFromLine className="w-5 h-5" />}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-lg text-emerald-950">{transactionType}</h3>
              <p className="text-xs mt-0.5 text-emerald-700/80">
                {transactionType === 'Cash In' ? 'Add cash directly to the safe' : 'Remove cash from the safe'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg shrink-0 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            
            <div className="flex p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setTransactionType('Cash In')}
                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${transactionType === 'Cash In' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Cash In
              </button>
              <button
                type="button"
                onClick={() => setTransactionType('Cash Out')}
                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${transactionType === 'Cash Out' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Cash Out
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <div className="relative">
                <CurrencyIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="input-field pl-9 font-mono"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="input-field min-h-[80px] resize-none"
                placeholder={transactionType === 'Cash In' ? 'E.g. Owner adding change to safe' : 'E.g. Owner removing cash'}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3.5 rounded-xl text-sm flex items-start gap-2 border border-red-100">
                <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 rounded-lg font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 focus-visible:ring-emerald-500"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {transactionType === 'Cash In' ? 'Confirm Cash In' : 'Confirm Cash Out'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary py-2.5">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
